import { useNavigate } from "@tanstack/react-router";
import { MoveLeft } from "lucide-react";
import { Button } from "../../components/ui/button";
import { useRuntime } from "../runtime/RuntimeContext";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

// Router-wide fallback for unmatched routes. Spacious, on-brand, themed via .notfound* tokens.
export function NotFoundPage() {
    useDocumentTitle("Page not found");
    const navigate = useNavigate();
    const runtime = useRuntime();
    const isAuthed = runtime.session.getAccessToken() != null;
    const home = isAuthed ? "/wc" : "/auth/login";
    const path = typeof window !== "undefined" ? window.location.pathname : "";

    return (
        <main className="notfound">
            <div className="notfound__panel">
                <svg
                    className="notfound__art"
                    viewBox="0 0 280 200"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <circle cx="138" cy="98" r="84" fill="currentColor" fillOpacity="0.06" />
                    <path
                        d="M36 152 C 78 104, 104 162, 146 116 S 214 74, 240 58"
                        stroke="currentColor"
                        strokeOpacity="0.5"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray="1.5 13"
                    />
                    <circle cx="36" cy="152" r="13" fill="currentColor" fillOpacity="0.16" />
                    <circle cx="36" cy="152" r="6.5" fill="currentColor" />
                    <g transform="rotate(13 240 54)">
                        <path
                            d="M240 24c-15.5 0-28 12-28 27 0 19 28 43 28 43s28-24 28-43c0-15-12.5-27-28-27z"
                            fill="currentColor"
                        />
                        <circle cx="240" cy="51" r="13" className="notfound__pin-hole" />
                        <text
                            x="240"
                            y="57"
                            textAnchor="middle"
                            fontSize="18"
                            fontWeight="700"
                            fontFamily="Ubuntu, sans-serif"
                            fill="currentColor"
                        >
                            ?
                        </text>
                    </g>
                </svg>

                <p className="notfound__eyebrow">404 · Page not found</p>
                <h1 className="notfound__title">You&rsquo;ve wandered off the route</h1>
                <p className="notfound__text">
                    The page you&rsquo;re looking for doesn&rsquo;t exist or has moved. Let&rsquo;s get you back on track.
                </p>

                <div className="notfound__actions">
                    <Button size="lg" onClick={() => navigate({ to: home } as Parameters<typeof navigate>[0])}>
                        {isAuthed ? "Go to workspace" : "Go to sign in"}
                    </Button>
                    <Button size="lg" variant="outline" onClick={() => window.history.back()}>
                        <MoveLeft />
                        Go back
                    </Button>
                </div>

                {path ? <p className="notfound__hint">{path}</p> : null}
            </div>
        </main>
    );
}
